import type { HtmlFromDotcms } from '../../converters-from-dotcms';
import {
  convertDotcmsBoolean,
  convertDotcmsResponseToRecord,
  wrapDotcmsHtml,
  convertDotcmsString,
} from '../../converters-from-dotcms';
import type { IBaseObjectData } from '../../dotcms-base-object';
import type { IImageData } from '../../dotcms-image';
import { ImageGqlHelper } from '../../dotcms-image';
import type { IDotcmsItemRestrictedBasedOnPlanType } from '../../dotcms-item-restricted-based-on-plan-type';
import type { IDotcmsItemRestrictedBasedOnCountryJurisdiction } from '../../dotcms-item-restricted-based-on-country-jurisdiction';
import { processAllowOrBlockList } from '../../dotcms-item-restricted-based-on-country-jurisdiction';
import type { IGqlHelper } from '../../gql-helper';
import { buildGqlFieldsQuery } from '../../gql-builders';
import { CategoryGqlHelper } from '../../dotcms-category';
import { DotKeyValueGqlHelper } from '../../dotcms-dotkeyvalue';
import { getActiveDotcmsPlanType } from '../../dotcms-plan-type-category';
import { processGqlDataAsArray } from '../../../use-builders-to-process-gql-data';

export type IResourceWithThumbnailData = IBaseObjectData &
IDotcmsItemRestrictedBasedOnPlanType &
IDotcmsItemRestrictedBasedOnCountryJurisdiction & {
  thumbnail?: IImageData;
  linkText: HtmlFromDotcms;
  titleText: HtmlFromDotcms;
  descriptionText?: HtmlFromDotcms;
  uniqueIdentifierForClickTracking?: string;
  authenticationRequiredToView: boolean;
};

export const ResourceWithThumbnailGqlHelper: IGqlHelper<IResourceWithThumbnailData> =
  {
    contentTypeName: null,
    gqlFieldsQuery: ` 
    thumbnail {${buildGqlFieldsQuery(ImageGqlHelper)}}
    linkText
    titleText
    descriptionText
    uniqueIdentifierForClickTracking
    authenticationRequiredToView
    restrictToPlanType { ${buildGqlFieldsQuery(CategoryGqlHelper)} }
    countryAllowList { ${DotKeyValueGqlHelper.gqlFieldsQuery} }
    countryBlockList { ${DotKeyValueGqlHelper.gqlFieldsQuery} }
    `,
    processGqlData: (gqlResponse: unknown) => {
      if (!gqlResponse) {
        return undefined;
      }

      const {
        thumbnail,
        linkText,
        titleText,
        descriptionText,
        uniqueIdentifierForClickTracking,
        restrictToPlanType,
        countryAllowList,
        countryBlockList,
        authenticationRequiredToView,
      } = convertDotcmsResponseToRecord(gqlResponse);

      return {
        thumbnail: ImageGqlHelper.processGqlData(thumbnail),
        linkText: wrapDotcmsHtml(linkText),
        titleText: wrapDotcmsHtml(titleText),
        descriptionText: wrapDotcmsHtml(descriptionText),
        uniqueIdentifierForClickTracking: convertDotcmsString(
          uniqueIdentifierForClickTracking,
        ),
        restrictToPlanType: getActiveDotcmsPlanType(
          processGqlDataAsArray(
            CategoryGqlHelper,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            restrictToPlanType,
          ),
        ),
        countryAllowList: processAllowOrBlockList(
          processGqlDataAsArray(
            DotKeyValueGqlHelper,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            countryAllowList,
          ),
        ),
        countryBlockList: processAllowOrBlockList(
          processGqlDataAsArray(
            DotKeyValueGqlHelper,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            countryBlockList,
          ),
        ),
        authenticationRequiredToView: !!convertDotcmsBoolean(
          authenticationRequiredToView,
        ),
      };
    },
  };
